import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import * as c from 'shared/constants'
import * as b from 'shared/constants/geographies'
import L from 'leaflet'
import 'leaflet.markercluster'
import { geographySelectionToString } from 'shared/utilities/languageUtils'
import { push } from 'connected-react-router'

import { MapContainer, TileLayer, Popup, useMap, useMapEvents } from 'react-leaflet'
import { Button } from 'react-bootstrap'
import GeographyGeoJson from 'LeafletMap/GeographyGeoJson'
import GeographyMarkerLabels from 'LeafletMap/GeographyMarkerLabels'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBug } from '@fortawesome/free-solid-svg-icons'
import PropertyIcons from 'LeafletMap/PropertyIcons'
import MapboxGLBaseLayer from 'LeafletMap/MapboxGLBaseLayer'
import SpinnerLoader from 'shared/components/Loaders/SpinnerLoader'

import './style.scss'

const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || ''
const USE_MAPBOX_GL_BASE = process.env.REACT_APP_MAPBOX_GL_BASE === 'true'
const MAPBOX_GL_STYLE =
  process.env.REACT_APP_MAPBOX_STYLE_URL || 'mapbox://styles/anhdnyc/cmtbq1lep000o01sle2bb4y1v'
const mapboxStyleUrl = (owner, styleId, suffix = '') => {
  return `https://api.mapbox.com/styles/v1/${owner}/${styleId}/tiles/256/{z}/{x}/{y}?access_token=${MAPBOX_ACCESS_TOKEN}${suffix ? `&${suffix}` : ''}`
}

const PIN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="20" height="30">
  <path d="M12 0C5.373 0 0 5.373 0 12c0 9.6 12 24 12 24S24 21.6 24 12C24 5.373 18.627 0 12 0z" fill="#1465b7" stroke="rgba(0,0,0,0.3)" stroke-width="1"/>
  <circle cx="12" cy="11" r="4" fill="white" opacity="0.85"/>
</svg>`

const MAP_PIN_ICON = L.divIcon({
  html: PIN_SVG,
  className: '',
  iconSize: L.point(20, 30),
  iconAnchor: L.point(10, 30),
})

// Inner functional component — uses hooks to give the class component access to the Leaflet map instance.
// Also owns the window resize listener so we can use useEffect for proper cleanup,
// replacing the componentDidMount/componentWillUnmount pattern in the parent class component.
// (LeafletMap must remain a class component because it hosts a React error boundary via
// getDerivedStateFromError + componentDidCatch, which are not supported in function components.)
const MapController = ({ onMapReady, onMapClick, onResize }) => {
  const map = useMap()

  useEffect(() => {
    if (map) onMapReady(map)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Attach resize listener here so the cleanup runs when MapContainer unmounts,
  // avoiding the manual removeEventListener in componentWillUnmount.
  useEffect(() => {
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [onResize])

  useMapEvents({
    click: onMapClick,
  })

  return null
}

// Scales geography district number pills down at city-wide zoom on every map that
// uses GeographyMarkerLabels (dashboard, custom search, homepage select).
const geographyLabelScale = zoom => Math.max(0.68, Math.min(1, (zoom - 9.5) / 4.5))

const MapGeographyLabelScale = () => {
  const map = useMap()

  useEffect(() => {
    const update = () => {
      map.getContainer().style.setProperty(
        '--geography-label-scale',
        String(geographyLabelScale(map.getZoom()))
      )
    }
    update()
    map.on('zoom zoomend', update)
    return () => {
      map.off('zoom zoomend', update)
      map.getContainer().style.removeProperty('--geography-label-scale')
    }
  }, [map])

  return null
}

export default class LeafletMap extends React.PureComponent {
  constructor(props) {
    super(props)
    this.mapContainerRef = React.createRef()
    this.mapInstance = null // set via MapController callback once map is ready
    this.geoJsonRef = React.createRef()
    this.clusterGroup = null
    this.state = {
      height: this.props.height,
      hasError: false,
      clusterGroup: null,
    }
    this.updateDimensions = this.updateDimensions.bind(this)
    this.centerMapOnGeography = this.centerMapOnGeography.bind(this)
    this.getGeographyBounds = this.getGeographyBounds.bind(this)
    this.getGeographyCenter = this.getGeographyCenter.bind(this)
    this.allGeographiesLoaded = this.allGeographiesLoaded.bind(this)
    this.handleMapClick = this.handleMapClick.bind(this)
    this.handleMapReady = this.handleMapReady.bind(this)
    this.setupClustering = this.setupClustering.bind(this)
    this.defaultPropertyAction = this.defaultPropertyAction.bind(this)
  }

  handleMapReady(map) {
    this.mapInstance = map
    this.updateDimensions()
    this.centerMapOnGeography()
    if (map.setZoom) {
      map.setZoom(this.props.zoom)
    }
    if (this.props.iconConfig === 'MULTIPLE' && this.props.results && this.props.results.length > 0) {
      this.setupClustering()
    }

    // Keep Leaflet's internal size synced to the container. Window resize alone
    // misses container-size changes (filter sidebar collapse, layout reflow),
    // which leaves a stale size and renders grey/unloaded tiles on zoom.
    if (typeof ResizeObserver !== 'undefined' && this.mapContainerRef.current) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.mapInstance && this.mapInstance.invalidateSize) {
          this.mapInstance.invalidateSize()
        }
      })
      this.resizeObserver.observe(this.mapContainerRef.current)
    }
  }

  componentDidUpdate(prevProps) {
    if (!this.mapInstance) return

    if (this.mapInstance.invalidateSize) {
      this.mapInstance.invalidateSize()
    }

    if (this.props.loading) return
    this.centerMapOnGeography()

    // Re-cluster on ANY results change (every advanced-search filter change
    // produces a new results reference), so the markers always reflect the
    // current filter. The `length > 0` guard from prior code was wrong:
    // an empty results array still needs to clear stale markers from the
    // previous filter. setupClustering() handles both cases (creates the
    // cluster group on first call, clearLayers on subsequent).
    if (this.props.iconConfig === 'MULTIPLE' &&
        prevProps.results !== this.props.results) {
      this.setupClustering()
    }
  }

  // Performance note (2026-06-24): dropped the >5000 "Proceed Anyway"
  // performance-warning modal. The cluster setup already configures
  // chunkedLoading + chunkInterval + removeOutsideVisibleBounds, which lets
  // leaflet.markercluster handle 50K+ markers smoothly even on mobile.
  // Plus the old modal had two bugs: its X button used jQuery to hide the
  // element without touching React state (leaving showPerformanceWarning
  // stuck true so subsequent filter changes never re-clustered), and only
  // the "Proceed Anyway" branch called setupClustering(). If real-world
  // mobile reports come in showing actual freeze, the right replacement is
  // a non-blocking toast on mobile-only (window.innerWidth < 768) — the
  // existing MapAlertModal component is reusable for that if we fix the X
  // handler to call action() too instead of jQuery-hiding. Don't bring
  // back blocking modals.

  componentWillUnmount() {
    // Resize listener cleanup is handled by MapController's useEffect return.
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    if (this.clusterGroup) {
      this.clusterGroup.clearLayers()
    }
  }

  setupClustering() {
    if (!this.mapInstance) return

    if (!this.clusterGroup) {
      this.clusterGroup = L.markerClusterGroup({
        chunkedLoading: true,
        maxClusterRadius: 100,
        spiderfyOnMaxZoom: false,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        removeOutsideVisibleBounds: true,
        animate: false,
        chunkInterval: 100,
        chunkDelay: 10,
        singleMarkerMode: false,
        iconCreateFunction: function(cluster) {
          const count = cluster.getChildCount()
          if (count === 1) {
            return MAP_PIN_ICON
          }
          const className = count < 10 ? 'marker-cluster-small' : count < 100 ? 'marker-cluster-medium' : 'marker-cluster-large'
          return L.divIcon({
            html: `<div><span>${count}</span></div>`,
            className: `marker-cluster ${className}`,
            iconSize: L.point(40, 40),
          })
        },
        animateAddingMarkers: false,
        maxZoom: 18,
        disableClusteringAtZoom: 19,
        minClusterSize: 10
      })

      this.mapInstance.addLayer(this.clusterGroup)
      this.clusterGroup.bringToFront()
      // Store in state so PropertyIcons re-renders and receives it
      this.setState({ clusterGroup: this.clusterGroup })
    } else {
      this.clusterGroup.clearLayers()
    }
  }

  defaultPropertyAction(property) {
    if (property && property.bbl) {
      this.props.dispatch(push(`/property/${property.bbl}`))
    }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch() {
    return null
  }

  handleMapClick() {
    if (this.props.closeGeographyPopup) {
      this.props.closeGeographyPopup()
    }
  }

  cityBoroughLatLng(changingOrCurrentType, changingOrCurrentId) {
    if (changingOrCurrentType === b.BOROUGH_GEOGRAPHY.constant) {
      switch (changingOrCurrentId) {
        case 'MN':
          return [40.7677746, -74.0032918]
        case 'BX':
          return [40.8489779, -73.8893546]
        case 'BK':
          return [40.6394277, -74.011271]
        case 'QN':
          return [40.6777619, -73.9721773]
        case 'SI':
          return [40.5619124, -74.2069121]
      }
    } else if (changingOrCurrentType === b.CITY_GEOGRAPHY.constant) {
      return [40.6752793, -74.0847861]
    }
  }

  centerMapOnGeography() {
    if (this.mapInstance) {
      const changingOrCurrentType = this.props.changingGeographyType || this.props.currentGeographyType
      const changingOrCurrentId = this.props.changingGeographyId || this.props.currentGeographyId

      if (!(changingOrCurrentType && changingOrCurrentId) && changingOrCurrentType !== b.CITY_GEOGRAPHY.constant) return

      if (
        changingOrCurrentType !== b.BOROUGH_GEOGRAPHY.constant &&
        changingOrCurrentType !== b.CITY_GEOGRAPHY.constant
      ) {
        const bounds = this.getGeographyBounds(changingOrCurrentType, changingOrCurrentId)

        if (bounds) {
          this.mapInstance.fitBounds(bounds, { padding: [-100, 0] })
        }
      } else {
        const latLng = this.cityBoroughLatLng(changingOrCurrentType, changingOrCurrentId)
        const zoom = changingOrCurrentType === b.BOROUGH_GEOGRAPHY.constant ? 11 : 10
        this.mapInstance.setView(latLng, zoom)
      }
    }
  }

  getGeographyBounds(type, id) {
    if (type === b.BOROUGH_GEOGRAPHY.constant) return null
    if (type === b.CITY_GEOGRAPHY.constant) return null
    if (!this.allGeographiesLoaded()) return null

    const geographyDataset = this.props.selectGeographyData(type)
    const selectedGeography = geographyDataset.find(geography => String(geography.id) === String(id))

    if (!selectedGeography) return

    return new L.geoJSON(selectedGeography.data.geometry).getBounds()
  }

  getGeographyCenter(type, id) {
    const bounds = this.getGeographyBounds(type, id)
    if (bounds) return bounds.getCenter()
    else return this.props.center
  }

  updateDimensions() {
    if (this.props.height) return
    if (this.mapContainerRef.current && this.mapInstance) {
      this.mapInstance.invalidateSize()
      this.setState({ height: this.mapContainerRef.current.offsetWidth })
    }
  }

  allGeographiesLoaded() {
    return (
      !this.props.loading &&
      !!this.props.communityDistricts.length &&
      !!this.props.councilDistricts.length &&
      !!this.props.stateAssemblies &&
      !!this.props.stateSenates &&
      !!this.props.zipCodes
    )
  }

  render() {
    if (this.state.hasError)
      return (
        <div className="map-error p-4 bg-light rounded">
          <h4>
            <FontAwesomeIcon icon={faBug} />
          </h4>
          Sorry, a map error occurred. Please reload the page or{' '}
          <a href={`mailto:${c.CONTACT_EMAIL}`}>contact support</a> if this continues to occur.
        </div>
      )
    return (
      <div
        id="map"
        ref={this.mapContainerRef}
        className={this.props.className}
        style={{ height: this.props.height || this.state.height, width: this.props.width || '100%' }}
        role="region"
        aria-label="Interactive property map"
      >
        <MapContainer
          data-test-id="map"
          boxZoom={this.props.interactive}
          center={this.props.center}
          className="map"
          doubleClickZoom={this.props.interactive}
          dragging={this.props.interactive}
          id="leaflet-map"
          keyboard={this.props.interactive}
          minZoom={10}
          maxZoom={20}
          scrollWheelZoom={this.props.interactive}
          tap={this.props.interactive}
          touchZoom={this.props.interactive}
          zoom={this.props.zoom}
          zoomControl={this.props.interactive}
          zoomSnap={0.5}
          zoomDelta={0.5}
          wheelPxPerZoomLevel={120}
        >
          <MapController
            onMapReady={this.handleMapReady}
            onMapClick={this.handleMapClick}
            onResize={this.updateDimensions}
          />
          <MapGeographyLabelScale />
          {this.props.loading && !this.props.suppressMapLoader && (
            <div className="map-loader">
              <SpinnerLoader size="100px" />
            </div>
          )}
          {USE_MAPBOX_GL_BASE ? (
            <MapboxGLBaseLayer accessToken={MAPBOX_ACCESS_TOKEN} style={MAPBOX_GL_STYLE} />
          ) : (
            <TileLayer
              attribution="mapbox"
              url={mapboxStyleUrl('mapbox', 'dark-v11')}
              maxZoom={20}
              maxNativeZoom={18}
              keepBuffer={4}
            />
          )}
          {this.props.currentGeographyType === 'COMMUNITY' && (
            <TileLayer
              attribution="mapbox"
              url={mapboxStyleUrl('anhdnyc', 'cjtgnuw5v04ad1fs950fzmkn1')}
              maxZoom={20}
              maxNativeZoom={18}
            />
          )}
          {this.props.currentGeographyType === 'COUNCIL' && (
            <TileLayer
              attribution="mapbox"
              url={mapboxStyleUrl('anhdnyc', 'cly4kn8px00hi01qrebgf8n4w')}
              maxZoom={20}
              maxNativeZoom={18}
            />
          )}
          {this.props.currentGeographyType === 'STATE_ASSEMBLY' && (
            <TileLayer
              attribution="mapbox"
              url={mapboxStyleUrl('anhdnyc', 'ck30ai0u70fol1co74j7lql1g')}
              maxZoom={20}
              maxNativeZoom={18}
            />
          )}
          {this.props.currentGeographyType === 'STATE_SENATE' && (
            <TileLayer
              attribution="mapbox"
              url={mapboxStyleUrl('anhdnyc', 'cla7cvnlq000h15o9oiftl65e')}
              maxZoom={20}
              maxNativeZoom={18}
            />
          )}
          {this.props.currentGeographyType === 'ZIPCODE' && (
            <TileLayer
              attribution="mapbox"
              url={mapboxStyleUrl('anhdnyc', 'clah97ak5000p16lbtwn31yoz')}
              maxZoom={20}
              maxNativeZoom={18}
            />
          )}
          {this.allGeographiesLoaded() && this.props.currentGeographyType && (
            <>
              <GeographyGeoJson
                geoJsonRef={this.geoJsonRef}
                geographies={this.props.selectGeographyData(
                  this.props.changingGeographyType || this.props.currentGeographyType
                )}
                currentGeographyId={this.props.currentGeographyId}
                currentGeographyType={this.props.currentGeographyType}
                changingGeographyId={this.props.changingGeographyId}
                changingGeographyType={this.props.changingGeographyType}
                onClick={this.props.handleChangeGeographyId}
              />
              <GeographyMarkerLabels
                currentGeographyType={this.props.currentGeographyType}
                currentGeographyId={this.props.currentGeographyId}
                changingGeographyId={this.props.changingGeographyId}
                geographies={this.props.selectGeographyData(
                  this.props.changingGeographyType || this.props.currentGeographyType
                )}
              />
            </>
          )}
          {this.allGeographiesLoaded() &&
            this.props.changingGeographyType &&
            (this.props.currentGeographyId !== this.props.changingGeographyId &&
              this.props.changingGeographyId > 0) && (
              <Popup
                key={`${this.changingGeographyType}-${this.changingGeographyId}`}
                {...(this.props.closeGeographyPopup ? { eventHandlers: { remove: this.props.closeGeographyPopup } } : {})}
                closeOnClick={false}
                position={
                  this.getGeographyCenter(this.props.changingGeographyType, this.props.changingGeographyId) || {}
                }
              >
                <p>
                  {geographySelectionToString({
                    type: this.props.changingGeographyType,
                    id: this.props.changingGeographyId,
                  })}
                </p>
                <Button
                  onClick={() => this.props.handleChangeGeography({ geographyId: this.props.changingGeographyId })}
                >
                  Visit
                </Button>
              </Popup>
            )}
          <PropertyIcons
            dispatch={this.props.dispatch}
            page={this.props.page}
            results={this.props.results}
            iconConfig={this.props.iconConfig}
            visible={!(this.props.changingGeographyType && this.props.changingGeographyId)}
            clusterGroup={this.state.clusterGroup}
            handlePropertyAction={this.props.handlePropertyAction || this.defaultPropertyAction}
            onMarkersLoadingChange={this.props.onMarkersLoadingChange}
          />
        </MapContainer>
      </div>
    )
  }
}

LeafletMap.defaultProps = {
  center: [40.71, -73.98],
  councilDistricts: [],
  communityDistricts: [],
  stateAssemblies: [],
  stateSenates: [],
  zipCodes: [],
  iconConfig: 'MULTIPLE',
  interactive: true,
  loading: false,
  results: [],
  height: 0,
  zoom: 12,
}

LeafletMap.propTypes = {
  className: PropTypes.string,
  communityDistricts: PropTypes.array,
  councilDistricts: PropTypes.array,
  stateAssemblies: PropTypes.array,
  stateSenates: PropTypes.array,
  zipCodes: PropTypes.array,
  closeGeographyPopup: PropTypes.func,
  dispatch: PropTypes.func,
  iconConfig: PropTypes.string,
  results: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  interactive: PropTypes.bool,
  loading: PropTypes.bool,
  suppressMapLoader: PropTypes.bool,
  onMarkersLoadingChange: PropTypes.func,
  page: PropTypes.string,
}
